// ============================================================================
// FNE-BRIDGE - Store Utilisateurs (Zustand)
// ============================================================================

import { create } from 'zustand';
import type { User } from '../types/models';

// ============================================================================
// Types du state
// ============================================================================

interface UserState {
  // --- Liste ---
  users: User[];
  usersLoading: boolean;
  usersError: string | null;
  usersTotal: number;
  usersPage: number;
  usersLimit: number;

  // --- Utilisateur courant ---
  currentUser: User | null;
  currentUserLoading: boolean;

  // --- Filtres ---
  filters: {
    search: string;
    role: string;
    est_actif: boolean | undefined;
  };

  // --- Actions ---
  setUsers: (users: User[], total: number) => void;
  setUsersLoading: (loading: boolean) => void;
  setUsersError: (error: string | null) => void;
  setUsersPage: (page: number) => void;
  setUsersLimit: (limit: number) => void;
  setCurrentUser: (user: User | null) => void;
  setCurrentUserLoading: (loading: boolean) => void;
  addUser: (user: User) => void;
  updateUser: (id: string, updates: Partial<User>) => void;
  removeUser: (id: string) => void;
  setFilters: (filters: Partial<UserState['filters']>) => void;
  reset: () => void;
}

// ============================================================================
// Store
// ============================================================================

const initialFilters = {
  search: '',
  role: '',
  est_actif: undefined as boolean | undefined,
};

export const useUserStore = create<UserState>()((set) => ({
  users: [],
  usersLoading: false,
  usersError: null,
  usersTotal: 0,
  usersPage: 1,
  usersLimit: 20,
  currentUser: null,
  currentUserLoading: false,
  filters: { ...initialFilters },

  setUsers: (users, total) => set({ users, usersTotal: total, usersLoading: false }),
  setUsersLoading: (usersLoading) => set({ usersLoading }),
  setUsersError: (usersError) => set({ usersError, usersLoading: false }),
  setUsersPage: (usersPage) => set({ usersPage }),
  setUsersLimit: (usersLimit) => set({ usersLimit, usersPage: 1 }),

  setCurrentUser: (currentUser) => set({ currentUser, currentUserLoading: false }),
  setCurrentUserLoading: (currentUserLoading) => set({ currentUserLoading }),

  addUser: (user) =>
    set((state) => ({
      users: [user, ...state.users],
      usersTotal: state.usersTotal + 1,
    })),

  updateUser: (id, updates) =>
    set((state) => ({
      users: state.users.map((u) => (u.id === id ? { ...u, ...updates } : u)),
      currentUser:
        state.currentUser?.id === id
          ? { ...state.currentUser, ...updates }
          : state.currentUser,
    })),

  removeUser: (id) =>
    set((state) => ({
      users: state.users.filter((u) => u.id !== id),
      usersTotal: state.usersTotal - 1,
      currentUser: state.currentUser?.id === id ? null : state.currentUser,
    })),

  setFilters: (newFilters) =>
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
      usersPage: 1,
    })),

  reset: () =>
    set({
      users: [],
      usersLoading: false,
      usersError: null,
      usersTotal: 0,
      usersPage: 1,
      currentUser: null,
      currentUserLoading: false,
      filters: { ...initialFilters },
    }),
}));
